import { ScrollingModule as ExperimentalScrollingModule } from '@angular/cdk-experimental/scrolling'
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling'
import { NgClass } from '@angular/common'
import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Input, OnInit, runInInjectionContext, signal, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { FilterButtonComponent } from '@app/v2/shared/components/filter-button/filter-button.component'
import { FilterDropdownTriggerComponent } from '@app/v2/shared/components/filter-dropdown-trigger/filter-dropdown-trigger.component'
import { FilterOptionComponent } from '@app/v2/shared/components/filter-option/filter-option.component'
import { MobileAccordionComponent } from '@app/v2/shared/components/mobile-accordion/mobile-accordion.component'
import { MobileFilterDrawerComponent } from '@app/v2/shared/components/mobile-filter-drawer/mobile-filter-drawer.component'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { SearchFieldComponent } from '@app/v2/shared/components/search-field/search-field.component'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { MobileFilterDrawerService } from '@app/v2/shared/services/mobile-filter-drawer.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { GenericTableSelectionChange, GenericTableSort, GenericTableSortDirection } from '@app/v2/shared/types/generic-table.types'
import { buildFilterDropdownOptions } from '@app/v2/shared/utils/filter-dropdown-options.util'
import { isElementVisible, isNearScrollBottom, isViewportOverflowing } from '@app/v2/shared/utils/viewport-scroll.util'
import { catchError, EMPTY, exhaustMap, finalize, map, Subject, switchMap } from 'rxjs'
import { ArchivedItemsService } from '../../services/archived-items.service'
import { EventTimelineSyncService } from '../../services/event-timeline-sync.service'
import { ArchivedItem } from '../../types/archived-item.type'
import {
  ARCHIVED_ITEM_DATE_RANGE_FILTER_OPTIONS,
  ARCHIVED_ITEM_TYPE_FILTER_OPTIONS,
  ArchivedItemDateRangeFilter,
  ArchivedItemsFilters,
  ArchivedItemTypeFilter,
  countActiveArchivedItemFilters,
  DEFAULT_ARCHIVED_ITEMS_FILTERS,
  formatArchivedItemCustomDateRangeLabel,
  getArchivedItemTodayYmd,
  toArchivedItemsQueryFilters
} from '../../types/archived-items-filter.type'
import { DateRange } from '../event-dates-and-timings/components/date-range-picker/date-range-picker-content/date-range-picker-content.component'
import { DateRangePickerComponent } from '../event-dates-and-timings/components/date-range-picker/date-range-picker.component'
import { EventsTimelineEmptyStateComponent } from '../events-timeline/components/events-timeline-empty-state/events-timeline-empty-state.component'
import { ArchivedItemsSkeletonComponent } from './components/archived-items-skeleton/archived-items-skeleton.component'
import { DesktopArchivedItemsTableComponent } from './components/desktop-archived-items-table/desktop-archived-items-table.component'
import { MobileArchivedItemCardComponent } from './components/mobile-archived-item-card/mobile-archived-item-card.component'

const DELETE_ARCHIVED_ITEMS_MODAL_ID = 'delete-archived-items-modal'
const RESTORE_ARCHIVED_ITEMS_MODAL_ID = 'restore-archived-items-modal'

type ArchivedItemsConfirmAction = 'delete' | 'restore'

/**
 * Build a confirmation message for archived items.
 * @param items {ArchivedItem[]} - The archived items.
 * @param action {ArchivedItemsConfirmAction} - The action to perform.
 * @returns {string} - The confirmation message.
 */
function buildArchivedItemsConfirmMessage(items: ArchivedItem[], action: ArchivedItemsConfirmAction): string {
  const count = items.length
  const actionVerb = action === 'delete' ? 'permanently delete' : 'restore'

  if (count === 1) {
    const name = items[0]?.name
    const type = items[0]?.type
    return `Are you sure you want to ${actionVerb} <strong>${name}</strong> ${type.toLowerCase()}?
    ${actionVerb === 'permanently delete' ? `<br><br>ALL RELATED DATA WILL BE PERMANENTLY DELETED and cannot be reversed or recovered.` : ''}`
  }

  return `Are you sure you want to ${actionVerb} <strong>${count}</strong> archived items?
     ${actionVerb === 'permanently delete' ? `<br><br>ALL RELATED DATA WILL BE PERMANENTLY DELETED and cannot be reversed or recovered.` : ''}`
}

const ARCHIVED_ITEM_MENU_OPTIONS: DropdownOption[] = [
  {
    value: 'Restore',
    key: 'restore',
    prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_47191_14341)"><path d="M2.05025 2.05025C1.07129 3.02922 0.404602 4.2765 0.134506 5.63437C-0.13559 6.99224 0.00303356 8.3997 0.532848 9.67879C1.06266 10.9579 1.95987 12.0511 3.11101 12.8203C4.26216 13.5895 5.61554 14 7.00001 14C8.38448 14 9.73785 13.5895 10.889 12.8203C12.0401 12.0511 12.9373 10.9579 13.4672 9.67878C13.997 8.39969 14.1356 6.99223 13.8655 5.63436C13.5954 4.27649 12.9287 3.02921 11.9497 2.05025C10.637 0.737493 8.85651 -1.75034e-06 6.99999 0C5.14348 1.75035e-06 3.36301 0.737501 2.05025 2.05025ZM10.9965 6.7335C10.3175 6.05451 9.39658 5.67306 8.43634 5.67306C7.4761 5.67306 6.55518 6.05452 5.87618 6.73351L7.4167 8.27402L2.93388 8.27403V3.79118L4.47436 5.33169C5.08777 4.71821 5.86947 4.30062 6.72043 4.13181C7.57139 3.963 8.4533 4.05057 9.25445 4.38343C10.0556 4.71629 10.7399 5.27947 11.2207 6.0016C11.7015 6.72373 11.9571 7.57232 11.9552 8.43986C11.7992 7.79352 11.4674 7.20295 10.9965 6.7335Z" fill="#1C64F2"/></g><defs><clipPath id="clip0_47191_14341"><rect width="14" height="14" fill="white"/></clipPath></defs></svg>`
  },
  {
    value: 'Delete permanently',
    key: 'delete-permanently',
    textColorClass: 'tw-text-red-600',
    prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_47191_14349)"><path d="M12.7053 2.94737H9.85348V1.47368C9.85348 1.08284 9.70325 0.708001 9.43584 0.431632C9.16842 0.155263 8.80573 0 8.42755 0H5.5757C5.19752 0 4.83483 0.155263 4.56742 0.431632C4.30001 0.708001 4.14978 1.08284 4.14978 1.47368V2.94737H1.29792C1.10883 2.94737 0.927489 3.025 0.793783 3.16318C0.660076 3.30137 0.584961 3.48879 0.584961 3.68421C0.584961 3.87963 0.660076 4.06705 0.793783 4.20524C0.927489 4.34342 1.10883 4.42105 1.29792 4.42105H2.01089V12.5263C2.01089 12.9172 2.16112 13.292 2.42853 13.5684C2.69594 13.8447 3.05863 14 3.43681 14H10.5664C10.9446 14 11.3073 13.8447 11.5747 13.5684C11.8421 13.292 11.9924 12.9172 11.9924 12.5263V4.42105H12.7053C12.8944 4.42105 13.0758 4.34342 13.2095 4.20524C13.3432 4.06705 13.4183 3.87963 13.4183 3.68421C13.4183 3.48879 13.3432 3.30137 13.2095 3.16318C13.0758 3.025 12.8944 2.94737 12.7053 2.94737ZM5.5757 1.47368H8.42755V2.94737H5.5757V1.47368ZM6.28866 11.0526C6.28866 11.2481 6.21355 11.4355 6.07984 11.5737C5.94614 11.7118 5.76479 11.7895 5.5757 11.7895C5.38661 11.7895 5.20527 11.7118 5.07156 11.5737C4.93785 11.4355 4.86274 11.2481 4.86274 11.0526V5.89474C4.86274 5.69931 4.93785 5.5119 5.07156 5.37371C5.20527 5.23553 5.38661 5.15789 5.5757 5.15789C5.76479 5.15789 5.94614 5.23553 6.07984 5.37371C6.21355 5.5119 6.28866 5.69931 6.28866 5.89474V11.0526ZM9.14052 11.0526C9.14052 11.2481 9.0654 11.4355 8.93169 11.5737C8.79799 11.7118 8.61664 11.7895 8.42755 11.7895C8.23846 11.7895 8.05712 11.7118 7.92341 11.5737C7.78971 11.4355 7.71459 11.2481 7.71459 11.0526V5.89474C7.71459 5.69931 7.78971 5.5119 7.92341 5.37371C8.05712 5.23553 8.23846 5.15789 8.42755 5.15789C8.61664 5.15789 8.79799 5.23553 8.93169 5.37371C9.0654 5.5119 9.14052 5.69931 9.14052 5.89474V11.0526Z" fill="#E02424"/></g><defs><clipPath id="clip0_47191_14349"><rect width="14" height="14" fill="white"/></clipPath></defs></svg>`
  }
]

@Component({
  selector: 'app-archived-items',
  standalone: true,
  imports: [
    NgClass,
    ArchivedItemsSkeletonComponent,
    MobileArchivedItemCardComponent,
    DesktopArchivedItemsTableComponent,
    ProgressCircleComponent,
    DeleteModalComponent,
    ScrollingModule,
    ExperimentalScrollingModule,
    SearchFieldComponent,
    EventsTimelineEmptyStateComponent,
    FilterButtonComponent,
    FilterDropdownTriggerComponent,
    MobileFilterDrawerComponent,
    MobileAccordionComponent,
    FilterOptionComponent,
    DropdownComponent,
    DateRangePickerComponent
  ],
  providers: [ArchivedItemsService, MobileFilterDrawerService],
  templateUrl: './archived-items.component.html',
  styleUrl: './archived-items.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArchivedItemsComponent implements OnInit {
  @Input() eventId!: number
  @Input() organizationId!: number
  @Input() eventName!: string
  @Input() eventTimelineSync?: EventTimelineSyncService
  @ViewChild(CdkVirtualScrollViewport) private _viewport?: CdkVirtualScrollViewport
  @ViewChild('desktopCustomDateRangePicker') private _desktopCustomDateRangePicker?: DateRangePickerComponent
  @ViewChild('mobileCustomDateRangePicker') private _mobileCustomDateRangePicker?: DateRangePickerComponent

  public readonly archivedItemMenuOptions = ARCHIVED_ITEM_MENU_OPTIONS
  public readonly archivedItems = signal<ArchivedItem[]>([])
  public readonly total = signal(0)
  public readonly isInitialLoading = signal(false)
  public readonly isPaginating = signal(false)
  public readonly hasMore = computed(() => this.archivedItems().length < this.total())
  public readonly virtualScrollMinBufferPx = 300
  public readonly virtualScrollMaxBufferPx = 600
  public readonly perPage = 10
  public readonly itemsPendingDelete = signal<ArchivedItem[]>([])
  public readonly itemsPendingRestore = signal<ArchivedItem[]>([])
  public readonly selectedItemsSummary = computed(() => {
    const count = this.selectedItems().length
    const noun = count === 1 ? 'item' : 'items'
    return `(${count}) ${noun} selected`
  })
  public readonly deleteModalId = DELETE_ARCHIVED_ITEMS_MODAL_ID
  public readonly restoreModalId = RESTORE_ARCHIVED_ITEMS_MODAL_ID
  public readonly deleteConfirmMessage = computed(() => buildArchivedItemsConfirmMessage(this.itemsPendingDelete(), 'delete'))
  public readonly restoreConfirmMessage = computed(() => buildArchivedItemsConfirmMessage(this.itemsPendingRestore(), 'restore'))
  public readonly selectedItemsMap = signal<Map<string, ArchivedItem>>(new Map())
  public readonly selectedItems = computed(() => Array.from(this.selectedItemsMap().values()))
  public readonly sortState = signal<GenericTableSort>({ field: null, direction: GenericTableSortDirection.DESC })
  public readonly selectedKeys = computed(() => Array.from(this.selectedItemsMap().keys()))
  public readonly searchTerm = signal<string>('')
  public readonly isFilterDrawerOpen = computed(() => this._filterDrawerService.isOpen())
  public readonly typeFilterOptions = ARCHIVED_ITEM_TYPE_FILTER_OPTIONS
  public readonly dateRangeFilterOptions = ARCHIVED_ITEM_DATE_RANGE_FILTER_OPTIONS
  public readonly appliedFilters = signal<ArchivedItemsFilters>({ ...DEFAULT_ARCHIVED_ITEMS_FILTERS })
  public readonly draftFilters = signal<ArchivedItemsFilters>({ ...DEFAULT_ARCHIVED_ITEMS_FILTERS })
  public readonly activeFilterCount = computed(() => countActiveArchivedItemFilters(this.appliedFilters()))
  public readonly basicFieldType = BasicFieldType.DATE
  public readonly selectedTypeLabel = computed(() => ARCHIVED_ITEM_TYPE_FILTER_OPTIONS.find((option) => option.value === this.appliedFilters().type)?.label ?? 'All types')
  public readonly selectedDateRangeLabel = computed(() => {
    const filters = this.appliedFilters()
    if (filters.dateRange === 'custom') {
      return formatArchivedItemCustomDateRangeLabel(filters.customArchivedFrom, filters.customArchivedTo)
    }
    return ARCHIVED_ITEM_DATE_RANGE_FILTER_OPTIONS.find((option) => option.value === filters.dateRange)?.label ?? 'Any time'
  })
  public readonly typeFilterDropdownOptions = computed(() => buildFilterDropdownOptions(ARCHIVED_ITEM_TYPE_FILTER_OPTIONS, this.appliedFilters().type))
  public readonly dateRangeFilterDropdownOptions = computed(() => buildFilterDropdownOptions(ARCHIVED_ITEM_DATE_RANGE_FILTER_OPTIONS, this.appliedFilters().dateRange))
  public readonly isTypeFilterDropdownOpen = signal(false)
  public readonly isDateRangeFilterDropdownOpen = signal(false)
  public readonly archivedDatePickerMaxDate = getArchivedItemTodayYmd()

  private readonly _archivedItemsService = inject(ArchivedItemsService)
  private readonly _filterDrawerService = inject(MobileFilterDrawerService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _modalService = inject(ModalService)
  private readonly _resetRequests$ = new Subject<void>()
  private readonly _loadMoreRequests$ = new Subject<number>()
  private _currentPage = 1
  private _scrollAnimationFrame: number | null = null
  private _viewportSyncFrame: number | null = null
  private _viewportPostSyncFrame: number | null = null
  private _requestLoaderInitialized = false
  private _isDestroyed = false
  private readonly _alertService = inject(AlertService)

  constructor() {
    effect(() => {
      if (!this._filterDrawerService.isOpen()) return
      this.draftFilters.set({ ...this.appliedFilters() })
    })
  }

  ngOnInit(): void {
    this._destroyRef.onDestroy(() => {
      this._isDestroyed = true

      if (this._scrollAnimationFrame !== null) {
        cancelAnimationFrame(this._scrollAnimationFrame)
      }

      if (this._viewportSyncFrame !== null) {
        cancelAnimationFrame(this._viewportSyncFrame)
      }

      if (this._viewportPostSyncFrame !== null) {
        cancelAnimationFrame(this._viewportPostSyncFrame)
      }
    })

    this._initRequestLoaders()
    this._loadFirstPage()
  }

  /**
   * Build a stable row key for selection and tracking.
   * Arrow function so it stays bound when passed to child inputs.
   */
  public readonly rowKeyFn = (item: ArchivedItem): string => `${item.type}-${item.id}`

  /**
   * Track archived items in the virtual scroll viewport.
   * Arrow function so it stays bound when passed to child inputs.
   */
  public readonly trackByItemId = (_index: number, item: ArchivedItem): string => this.rowKeyFn(item)

  /**
   * Check whether an archived item is currently selected.
   * @param item {ArchivedItem} - The archived item.
   * @returns {boolean} - Whether the item is selected.
   */
  public isItemSelected(item: ArchivedItem): boolean {
    return this.selectedItemsMap().has(this.rowKeyFn(item))
  }

  /**
   * Toggle selection for an archived item.
   * @param item {ArchivedItem} - The archived item.
   * @returns {void} - The void.
   */
  public toggleItemSelection(item: ArchivedItem): void {
    const key = this.rowKeyFn(item)
    this.selectedItemsMap.update((current) => {
      const next = new Map(current)
      if (next.has(key)) next.delete(key)
      else next.set(key, item)
      return next
    })
  }

  /**
   * Sync selected items from the generic table selection output.
   * @param change {GenericTableSelectionChange<ArchivedItem>} - The selection change.
   * @returns {void} - The void.
   */
  public onSelectionChange(change: GenericTableSelectionChange<ArchivedItem>): void {
    this.selectedItemsMap.update((current) => {
      const next = new Map<string, ArchivedItem>()
      const selectedKeys = new Set(change.keys)

      current.forEach((item, key) => {
        if (selectedKeys.has(key)) {
          next.set(key, item)
        }
      })

      for (const row of change.rows) {
        next.set(this.rowKeyFn(row), row)
      }

      return next
    })
  }

  /**
   * Update sort state and reload the first page.
   * @param sort {GenericTableSort} - The sort state.
   * @returns {void} - The void.
   */
  public onSortChange(sort: GenericTableSort): void {
    this.sortState.set(sort)
    this._loadFirstPage()
  }

  /**
   * Open the restore confirmation modal for selected items.
   */
  public openRestoreModal(): void {
    this._openRestoreModalForItems(this.selectedItems())
  }

  /**
   * Open the delete permanently confirmation modal for selected items.
   */
  public openDeleteModal(): void {
    this._openDeleteModalForItems(this.selectedItems())
  }

  /**
   * Handle row action menu selection from desktop or mobile archived item views.
   * @param payload {ArchivedItem} - The archived item.
   * @param option {DropdownOption} - The dropdown option.
   * @returns {void} - The void.
   */
  public onMenuOptionClick(payload: { item: ArchivedItem; option: DropdownOption }): void {
    if (payload.option.key === 'restore') {
      this._openRestoreModalForItems([payload.item])
      return
    }

    if (payload.option.key === 'delete-permanently') {
      this._openDeleteModalForItems([payload.item])
    }
  }

  /**
   * Whether the restore confirmation modal is currently open.
   */
  public isRestoreModalOpen(): boolean {
    return this._modalService.isModalOpen(this.restoreModalId)
  }

  /**
   * Whether the delete confirmation modal is currently open.
   */
  public isDeleteModalOpen(): boolean {
    return this._modalService.isModalOpen(this.deleteModalId)
  }

  /**
   * Confirm restoration of archived items pending in the modal.
   */
  public confirmRestore(): void {
    const items = this.itemsPendingRestore()

    this._archivedItemsService
      .restoreArchivedItems(
        this.organizationId,
        this.eventId,
        items.map((item) => ({ id: item.id, type: item.type }))
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          this._alertService.alert(Alert.SUCCESS, res.message)
          this._removeItemsFromList(items)
          this.itemsPendingRestore.set([])
          this.eventTimelineSync?.requestEventPersonasRefresh(this.eventId)
        },
        error: (err) => this._alertService.alert(Alert.ERROR, err.error.message)
      })
  }

  /**
   * Confirm permanent deletion of archived items pending in the modal.
   */
  public confirmDeletePermanently(): void {
    const items = this.itemsPendingDelete()

    this._archivedItemsService
      .deleteArchivedItems(
        this.organizationId,
        this.eventId,
        items.map((item) => ({ id: item.id, type: item.type }))
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          this._alertService.alert(Alert.SUCCESS, res.message)
          this._removeItemsFromList(items)

          const deletedKeys = new Set(this.itemsPendingDelete().map((item) => this.rowKeyFn(item)))
          this.selectedItemsMap.update((current) => {
            const next = new Map(current)
            for (const key of Array.from(deletedKeys)) {
              next.delete(key)
            }
            return next
          })
          this.itemsPendingDelete.set([])

          this._loadFirstPage()
        },
        error: (err) => this._alertService.alert(Alert.ERROR, err.error.message)
      })
  }

  /**
   * Close the restore confirmation modal.
   */
  public cancelRestore(): void {
    this.itemsPendingRestore.set([])

    if (this._modalService.isModalOpen(this.restoreModalId)) {
      this._modalService.toggleModal(this.restoreModalId)
    }
  }

  /**
   * Close the delete confirmation modal.
   */
  public cancelDeletePermanently(): void {
    this.itemsPendingDelete.set([])

    if (this._modalService.isModalOpen(this.deleteModalId)) {
      this._modalService.toggleModal(this.deleteModalId)
    }
  }

  /**
   * Open the restore confirmation modal for the given items.
   * @param items {ArchivedItem[]} - The archived items.
   * @returns {void} - The void.
   */
  private _openRestoreModalForItems(items: ArchivedItem[]): void {
    if (!items.length) return

    this.itemsPendingRestore.set(items)

    if (!this._modalService.isModalOpen(this.restoreModalId)) {
      this._modalService.toggleModal(this.restoreModalId)
    }
  }

  /**
   * Open the delete permanently confirmation modal for the given items.
   * @param items {ArchivedItem[]} - The archived items.
   * @returns {void} - The void.
   */
  private _openDeleteModalForItems(items: ArchivedItem[]): void {
    if (!items.length) return

    this.itemsPendingDelete.set(items)

    if (!this._modalService.isModalOpen(this.deleteModalId)) {
      this._modalService.toggleModal(this.deleteModalId)
    }
  }

  /**
   * Remove restored or deleted items from the list and selection state.
   * @param itemsToRemove {ArchivedItem[]} - The archived items to remove.
   * @returns {void} - The void.
   */
  private _removeItemsFromList(itemsToRemove: ArchivedItem[]): void {
    const keysToRemove = new Set(itemsToRemove.map((item) => this.rowKeyFn(item)))

    this.archivedItems.update((current) => current.filter((item) => !keysToRemove.has(this.rowKeyFn(item))))
    this.selectedItemsMap.update((current) => {
      const next = new Map(current)
      for (const key of Array.from(keysToRemove)) {
        next.delete(key)
      }
      return next
    })
    this.total.update((total) => Math.max(0, total - itemsToRemove.length))
  }

  /**
   * Load the next page from the generic table infinite scroll.
   * @returns {void} - The void.
   */
  public loadNextPage(): void {
    this._loadMoreRequests$.next(this._currentPage + 1)
  }

  /**
   * Load the next page when the mobile virtual scroll viewport nears the bottom.
   */
  public onViewportScroll(): void {
    const viewport = this._viewport?.elementRef.nativeElement
    if (!viewport) return

    if (this._scrollAnimationFrame !== null) {
      cancelAnimationFrame(this._scrollAnimationFrame)
    }

    this._scrollAnimationFrame = requestAnimationFrame(() => {
      this._scrollAnimationFrame = null

      if (this.isInitialLoading() || this.isPaginating() || !this.hasMore()) return

      if (isNearScrollBottom(viewport)) this.loadNextPage()
    })
  }

  /**
   * Request the first page (used on init and sort reset).
   */
  private _loadFirstPage(): void {
    this._resetRequests$.next()
  }

  /**
   * Initialize reset and pagination loaders.
   */
  private _initRequestLoaders(): void {
    if (this._requestLoaderInitialized) return

    this._requestLoaderInitialized = true

    this._resetRequests$
      .pipe(
        switchMap(() => {
          this.isInitialLoading.set(true)
          this._currentPage = 1

          const sort = this.sortState()
          const sortParams = sort.field ? { field: sort.field, direction: sort.direction } : undefined
          const search = this.searchTerm().trim() || undefined
          const filters = toArchivedItemsQueryFilters(this.appliedFilters())

          return this._archivedItemsService.getArchivedItems(this.organizationId, this.eventId, 1, this.perPage, sortParams, search, filters).pipe(
            map((response) => response),
            catchError((err) => {
              this._alertService.alert(Alert.ERROR, err.error.message)
              this.archivedItems.set([])
              this.total.set(0)
              return EMPTY
            }),
            finalize(() => {
              this.isInitialLoading.set(false)
            })
          )
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((response) => this._applyPageResponse(response, true))

    this._loadMoreRequests$
      .pipe(
        exhaustMap((page) => {
          if (this._isDestroyed || this.isInitialLoading() || this.isPaginating() || !this.hasMore()) return EMPTY
          this.isPaginating.set(true)

          return this._fetchPage(page, false).pipe(
            finalize(() => {
              this.isPaginating.set(false)
            })
          )
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((response) => this._applyPageResponse(response, false))
  }

  /**
   * Fetch archived items for a specific page.
   */
  private _fetchPage(page: number, resetOnError: boolean) {
    const sort = this.sortState()
    const sortParams = sort.field ? { field: sort.field, direction: sort.direction } : undefined
    const search = this.searchTerm().trim() || undefined
    const filters = toArchivedItemsQueryFilters(this.appliedFilters())

    return this._archivedItemsService.getArchivedItems(this.organizationId, this.eventId, page, this.perPage, sortParams, search, filters).pipe(
      catchError((err) => {
        this._alertService.alert(Alert.ERROR, err.error.message)
        if (resetOnError) {
          this.archivedItems.set([])
          this.total.set(0)
          this._currentPage = 1
        }
        return EMPTY
      })
    )
  }

  /**
   * Apply a successful page response to component state.
   * @param response {ArchivedItemResponse} - The response.
   * @param reset {boolean} - Whether to reset the component state.
   * @returns {void} - The void.
   */
  private _applyPageResponse(response: { data: ArchivedItem[]; total: number; page: number }, reset: boolean): void {
    const items = response.data
    this.total.set(response.total)
    this._currentPage = response.page

    if (reset) this.archivedItems.set(items)
    else this.archivedItems.update((current) => [...current, ...items])

    this._syncViewportAfterDataChange()
  }

  /**
   * Recalculate mobile virtual scroll dimensions after the DOM paints new rows.
   */
  private _syncViewportAfterDataChange(): void {
    if (this._isDestroyed) return

    if (this._viewportSyncFrame !== null) {
      cancelAnimationFrame(this._viewportSyncFrame)
    }

    if (this._viewportPostSyncFrame !== null) {
      cancelAnimationFrame(this._viewportPostSyncFrame)
    }

    this._viewportSyncFrame = requestAnimationFrame(() => {
      this._viewportSyncFrame = null
      if (this._isDestroyed) return

      this._viewport?.checkViewportSize()

      this._viewportPostSyncFrame = requestAnimationFrame(() => {
        this._viewportPostSyncFrame = null
        if (this._isDestroyed) return

        this._viewport?.checkViewportSize()
        this._loadMoreIfViewportNotFilled()
      })
    })
  }

  /**
   * Fetch the next page when mobile content does not fill the virtual scroll viewport.
   */
  private _loadMoreIfViewportNotFilled(): void {
    if (this.isInitialLoading() || this.isPaginating() || !this.hasMore()) return

    const element = this._viewport?.elementRef.nativeElement
    if (!element || !isElementVisible(element)) return

    if (this.archivedItems().length >= this.perPage) return

    if (!isViewportOverflowing(element)) this._loadMoreRequests$.next(this._currentPage + 1)
  }

  /**
   * Handle search change event.
   * @param search {string} - The search term.
   * @returns {void} - The void.
   */
  public onSearchChange(search: string): void {
    const trimmedSearch = search.trim()
    const previousSearch = this.searchTerm().trim()

    if (!trimmedSearch) {
      if (!previousSearch) return

      this.searchTerm.set('')
      this._loadFirstPage()
      return
    }

    this.searchTerm.set(trimmedSearch)
    this._loadFirstPage()
  }

  /**
   * Reset draft filters to defaults.
   */
  public onFilterReset(): void {
    this.draftFilters.set({ ...DEFAULT_ARCHIVED_ITEMS_FILTERS })
  }

  /**
   * Apply draft filters and reload the first page.
   */
  public onFilterApply(): void {
    const draft = { ...this.draftFilters() }

    if (draft.dateRange === 'custom' && (!draft.customArchivedFrom || !draft.customArchivedTo)) {
      const applied = this.appliedFilters()
      if (applied.dateRange === 'custom' && applied.customArchivedFrom && applied.customArchivedTo) {
        draft.customArchivedFrom = applied.customArchivedFrom
        draft.customArchivedTo = applied.customArchivedTo
      } else {
        draft.dateRange = 'any'
        draft.customArchivedFrom = null
        draft.customArchivedTo = null
      }
    }

    this.appliedFilters.set(draft)
    this._loadFirstPage()
    this._filterDrawerService.close()
  }

  /**
   * Update the draft type filter.
   * @param type {ArchivedItemTypeFilter} - The type.
   * @returns {void} - The void.
   */
  public onTypeFilterSelect(type: ArchivedItemTypeFilter): void {
    this.draftFilters.update((filters) => ({ ...filters, type }))
  }

  /**
   * Update the draft date range filter.
   * @param dateRange {ArchivedItemDateRangeFilter} - The date range.
   * @returns {void} - The void.
   */
  public onDateRangeFilterSelect(dateRange: ArchivedItemDateRangeFilter): void {
    this.draftFilters.update((filters) => ({
      ...filters,
      dateRange,
      ...(dateRange === 'custom'
        ? {}
        : {
            customArchivedFrom: null,
            customArchivedTo: null
          })
    }))

    if (dateRange === 'custom') {
      this._openMobileCustomDateRangePicker()
    }
  }

  /**
   * Apply the selected type filter from the desktop dropdown.
   * @param option {DropdownOption} - The dropdown option.
   */
  public onTypeFilterDropdownSelect(option: DropdownOption): void {
    const type = option.key as ArchivedItemTypeFilter
    if (this.appliedFilters().type === type) return

    this.appliedFilters.update((filters) => ({ ...filters, type }))
    this._loadFirstPage()
  }

  /**
   * Apply the selected date range filter from the desktop dropdown.
   * @param option {DropdownOption} - The dropdown option.
   */
  public onDateRangeFilterDropdownSelect(option: DropdownOption): void {
    const dateRange = option.key as ArchivedItemDateRangeFilter

    if (dateRange === 'custom') {
      this._openDesktopCustomDateRangePicker()
      return
    }

    if (this.appliedFilters().dateRange === dateRange) return

    this.appliedFilters.update((filters) => ({
      ...filters,
      dateRange,
      customArchivedFrom: null,
      customArchivedTo: null
    }))
    this._loadFirstPage()
  }

  /**
   * Apply a custom archived date range from the desktop date picker.
   * @param range {DateRange} - The selected date range.
   */
  public onCustomDateRangeSelected(range: DateRange): void {
    if (!range.startDate || !range.endDate) return

    this.appliedFilters.update((filters) => ({
      ...filters,
      dateRange: 'custom',
      customArchivedFrom: range.startDate,
      customArchivedTo: range.endDate
    }))
    this._loadFirstPage()
  }

  /**
   * Reset every desktop filter to its default and reload the first page.
   */
  public onClearAllFilters(): void {
    this.appliedFilters.set({ ...DEFAULT_ARCHIVED_ITEMS_FILTERS })
    this._loadFirstPage()
  }

  /**
   * Update draft filters when a custom date range is selected in the mobile drawer.
   * @param range {DateRange} - The selected date range.
   */
  public onDraftCustomDateRangeSelected(range: DateRange): void {
    if (!range.startDate || !range.endDate) return

    this.draftFilters.update((filters) => ({
      ...filters,
      dateRange: 'custom',
      customArchivedFrom: range.startDate,
      customArchivedTo: range.endDate
    }))
  }

  /**
   * Open the desktop custom date range picker after the filter dropdown closes.
   */
  private _openDesktopCustomDateRangePicker(): void {
    queueMicrotask(() => this._desktopCustomDateRangePicker?.openPickerFromTrigger())
  }

  /**
   * Open the mobile custom date range picker after the custom option is selected in the drawer.
   */
  private _openMobileCustomDateRangePicker(): void {
    afterNextRender(() => this._mobileCustomDateRangePicker?.openPickerFromTrigger())
  }
}
